import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ContentHeader.css';

/**
 * Standardized content header for Settings, Reports, and Lists pages.
 * Renders title + optional back link/button on the left, optional actions on the right.
 *
 * @param {string|React.ReactNode} title - Page title
 * @param {string} [backTo] - Path for back link. Omit to hide back link (unless onBack is used).
 * @param {string} [backLabel] - Label for back link/button (e.g. 'Back to Settings')
 * @param {function} [onBack] - Callback for back button (e.g. cancel edit). When provided without backTo, renders a button instead of Link.
 * @param {React.ReactNode} [actions] - Optional actions (e.g. Add button) to render on the right
 * @param {React.ReactNode} [children] - Optional extra content below the header row (e.g. status message)
 * @param {string|React.ReactNode} [titleHint] - Optional hint/description. Hidden by default; click title or info icon to toggle. Reduces visual noise.
 * @param {{ to: string, label: string }} [secondaryLink] - Optional second link rendered under the back link (e.g. Client Tracking).
 */
function ContentHeader({ title, backTo, backLabel, onBack, actions, children, titleHint, secondaryLink }) {
  const [hintExpanded, setHintExpanded] = useState(false);
  const showBack = backTo || onBack;
  const backLabelText = backLabel || 'Back';
  const hasHint = titleHint != null && titleHint !== '';

  return (
    <div className="content-header">
      <div className="content-header-row">
        <div className="content-header-left">
          {title != null && title !== '' && (
            <div className="content-header-title-block">
              <h1 className="content-header-title">
                {hasHint ? (
                  <button
                    type="button"
                    className="content-header-title-with-hint"
                    onClick={() => setHintExpanded((prev) => !prev)}
                    aria-expanded={hintExpanded}
                    aria-label={hintExpanded ? 'Hide description' : 'Show description'}
                  >
                    {title}
                    <span className="content-header-title-hint-icon" aria-hidden>ⓘ</span>
                  </button>
                ) : (
                  title
                )}
              </h1>
              {hasHint && hintExpanded && (
                <div className="content-header-hint">
                  {typeof titleHint === 'string' ? <p>{titleHint}</p> : titleHint}
                </div>
              )}
            </div>
          )}
          {showBack && (
            backTo ? (
              <Link to={backTo} className="link-nav link-back">
                ← {backLabelText}
              </Link>
            ) : (
              <button type="button" onClick={onBack} className="link-nav link-back content-header-back-btn">
                ← {backLabelText}
              </button>
            )
          )}
          {secondaryLink && (
            <Link to={secondaryLink.to} className="link-nav link-back">
              {secondaryLink.label}
            </Link>
          )}
        </div>
        {actions && <div className="content-header-actions">{actions}</div>}
      </div>
      {children && <div className="content-header-extra">{children}</div>}
    </div>
  );
}

export default ContentHeader;
